"use client";

import { useState } from "react";
import {
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  AnovaTable,
  CoefficientsTable,
  RegressionWorking,
  ResidualsTable,
  regressionEquation,
} from "@/components/tools/stats-descriptive/regression-output";
import { ScatterPlot } from "@/components/tools/stats-descriptive/scatter-plot";
import {
  Explanation,
  Formula,
  PairedDataInput,
  usePairedData,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  formatPercent,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  linearRegression,
  predictAt,
  type RegressionReport,
} from "@/lib/tool-kit/stats-descriptive/regression";

/** English strings. A translated content file overrides these through `ui`. */
export const LINEAR_REGRESSION_CALCULATOR_STRINGS = {
  entryLabel: "Data entry",
  entryLists: "Separate X and Y lists",
  entryLines: 'Pairs — one "x, y" per line',
  xLabel: "X values (predictor)",
  yLabel: "Y values (response)",
  xPlaceholder: "18, 20, 21, 23, 24",
  yPlaceholder: "52, 55, 61, 60, 68",
  pairsLabel: "Paired data — one x, y pair per line",
  pairsPlaceholder: "18, 52\n20, 55\n21, 61",
  valuesCount: "one: value|other: values",
  pairsCount: "one: pair read|other: pairs read",
  skippedCount:
    "one: , {count} line without two numbers skipped|other: , {count} lines without two numbers skipped",
  tryExample: "Try an example",
  exampleX: "18, 20, 21, 23, 24, 26, 27, 29, 30, 32",
  exampleY: "52, 55, 61, 60, 68, 70, 75, 74, 82, 85",
  examplePairs:
    "18, 52\n20, 55\n21, 61\n23, 60\n24, 68\n26, 70\n27, 75\n29, 74\n30, 82\n32, 85",
  examplePredict: "28",
  clear: "Clear",
  mismatch:
    "The two lists differ in length (X: {x}, Y: {y}). Each x needs a matching y.",
  enterPrompt:
    "Enter at least three (x, y) pairs, with at least two different x values.",
  results: "Results",
  equationLabel: "Least-squares line",
  equationPlus: "ŷ = {a} + {b}x",
  equationMinus: "ŷ = {a} − {b}x",
  slope: "Slope (b)",
  intercept: "Intercept (a)",
  correlation: "Correlation (r)",
  rSquared: "R²",
  see: "Standard error of the estimate",
  pairsN: "Pairs (n)",
  slopeUp: "Each one-unit increase in x raises the predicted y by {b}.",
  slopeDown: "Each one-unit increase in x lowers the predicted y by {b}.",
  slopeFlat: "The fitted line is flat: x does not change the predicted y.",
  r2Sentence: "R² = {r2}: the line accounts for {pct} of the variation in y.",
  plotTitle: "Scatter plot with the fitted line",
  plotLabel: "Scatter plot of {n} data points with the fitted line {equation}",
  axisX: "x",
  axisY: "y",
  coefficientsTitle: "Coefficients",
  colTerm: "Term",
  colEstimate: "Estimate",
  colSe: "Standard error",
  colT: "t",
  colP: "p-value",
  colCi: "95% confidence interval",
  termIntercept: "Intercept",
  termSlope: "Slope",
  ciTemplate: "{low} to {high}",
  anovaTitle: "Analysis of variance",
  colSource: "Source",
  colDf: "df",
  colSs: "Sum of squares",
  colMs: "Mean square",
  colF: "F",
  sourceRegression: "Regression",
  sourceResidual: "Residual",
  sourceTotal: "Total",
  predictTitle: "Predict y for a new x",
  predictLabel: "New x value",
  predictedY: "Predicted ŷ",
  confidenceInterval: "95% CI for the mean of y",
  predictionInterval: "95% prediction interval",
  predictWorking: "ŷ = {a} + {b} × {x} = {y}",
  extrapolation:
    "{x} lies outside the observed x range ({min} to {max}). That is extrapolation: nothing in the data shows the straight-line pattern continuing there, so treat the prediction with caution.",
  residualsTitle: "Residuals",
  colIndex: "#",
  colX: "x",
  colY: "y",
  colFitted: "ŷ",
  colResidual: "Residual (y − ŷ)",
  workingTitle: "How it was calculated",
  meansLine: "x̄ = {mx}, ȳ = {my}",
  sumsLine:
    "Sxx = Σ(x − x̄)² = {sxx}; Sxy = Σ(x − x̄)(y − ȳ) = {sxy}; Syy = Σ(y − ȳ)² = {syy}",
  slopeLine: "b = Sxy ÷ Sxx = {sxy} ÷ {sxx} = {b}",
  interceptLine: "a = ȳ − b × x̄ = {my} − {b} × {mx} = {a}",
  rLine: "r = {r}; R² = SSR ÷ SST = {ssr} ÷ {sst} = {r2}",
  seLine: "s = √(SSE ÷ (n − 2)) = √({sse} ÷ {df}) = {s}",
  seSlopeLine:
    "SE(b) = s ÷ √Sxx = {s} ÷ √{sxx} = {seb}; t = b ÷ SE(b) = {t} with {df} degrees of freedom",
  assumptionsNote:
    "The p-values and intervals assume independent errors that scatter evenly around the line and are roughly normal. Check the residuals: a curve or a funnel shape means a straight line is the wrong model.",
} as const;

function slopeSentence(
  report: RegressionReport,
  fmt: NumberFormatter,
  strings: typeof LINEAR_REGRESSION_CALCULATOR_STRINGS
): string {
  const b = formatStat(fmt, Math.abs(report.slope));
  if (report.slope > 0) {
    return t(strings.slopeUp, { b });
  }
  if (report.slope < 0) {
    return t(strings.slopeDown, { b });
  }
  return strings.slopeFlat;
}

function Prediction({
  report,
  fmt,
  strings,
}: {
  report: RegressionReport;
  fmt: NumberFormatter;
  strings: typeof LINEAR_REGRESSION_CALCULATOR_STRINGS;
}) {
  const [xText, setXText] = useState("");
  const x = parseNumberInput(xText);
  const prediction = x === null ? null : predictAt(report, x);
  const num = (value: number) => formatStat(fmt, value);
  const interval = ([low, high]: [number, number]) =>
    t(strings.ciTemplate, { low: num(low), high: num(high) });
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.predictTitle}</h3>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <TextField
            id="lr-predict"
            label={strings.predictLabel}
            numeric
            onChange={setXText}
            placeholder={strings.examplePredict}
            value={xText}
          />
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setXText(strings.examplePredict)}
          type="button"
        >
          {strings.tryExample}
        </button>
      </div>
      {prediction ? (
        <div aria-live="polite" className="space-y-3">
          <StatGrid>
            <StatCard
              emphasis
              label={strings.predictedY}
              value={num(prediction.fitted)}
            />
            <StatCard
              label={strings.confidenceInterval}
              value={interval(prediction.confidence)}
            />
            <StatCard
              label={strings.predictionInterval}
              value={interval(prediction.prediction)}
            />
          </StatGrid>
          <Formula>
            {t(strings.predictWorking, {
              a: num(report.intercept),
              b: num(report.slope),
              x: num(prediction.x),
              y: num(prediction.fitted),
            })}
          </Formula>
          {prediction.extrapolated ? (
            <Notice>
              {t(strings.extrapolation, {
                x: num(prediction.x),
                min: num(report.minX),
                max: num(report.maxX),
              })}
            </Notice>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function LinearRegressionCalculatorWidget() {
  const strings = useToolUi(LINEAR_REGRESSION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const data = usePairedData();
  const num = (value: number) => formatStat(fmt, value);

  const report = data.mismatched ? null : linearRegression(data.xs, data.ys);
  const showMismatch =
    data.mismatched && data.listXCount > 0 && data.listYCount > 0;
  const count = (n: number) => (
    <>
      {fmt(n)}
      {tPlural(strings.valuesCount, locale, n)}
    </>
  );
  const equation = report ? regressionEquation(report, fmt, strings) : "";

  return (
    <>
      <section className="space-y-4">
        <PairedDataInput
          data={data}
          idPrefix="lr"
          labels={{
            mode: strings.entryLabel,
            lists: strings.entryLists,
            lines: strings.entryLines,
            x: strings.xLabel,
            y: strings.yLabel,
            xPlaceholder: strings.xPlaceholder,
            yPlaceholder: strings.yPlaceholder,
            pairs: strings.pairsLabel,
            pairsPlaceholder: strings.pairsPlaceholder,
          }}
          pairsHint={
            <>
              {fmt(data.pairCount)}
              {tPlural(strings.pairsCount, locale, data.pairCount)}
              {data.skippedLines > 0
                ? tPlural(strings.skippedCount, locale, data.skippedLines, {
                    count: fmt(data.skippedLines),
                  })
                : null}
            </>
          }
          xHint={count(data.listXCount)}
          yHint={count(data.listYCount)}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              data.fill({
                x: strings.exampleX,
                y: strings.exampleY,
                pairs: strings.examplePairs,
              })
            }
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={data.clear}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {showMismatch ? (
        <Notice>
          {t(strings.mismatch, {
            x: fmt(data.listXCount),
            y: fmt(data.listYCount),
          })}
        </Notice>
      ) : null}

      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <div className={`${RESULT_CARD} text-center`}>
              <p className="text-muted-foreground text-sm">
                {strings.equationLabel}
              </p>
              <p className="break-words font-mono font-semibold text-2xl text-foreground">
                {equation}
              </p>
            </div>
            <StatGrid>
              <StatCard label={strings.slope} value={num(report.slope)} />
              <StatCard
                label={strings.intercept}
                value={num(report.intercept)}
              />
              <StatCard label={strings.correlation} value={num(report.r)} />
              <StatCard label={strings.rSquared} value={num(report.rSquared)} />
              <StatCard label={strings.see} value={num(report.see)} />
              <StatCard label={strings.pairsN} value={fmt(report.n)} />
            </StatGrid>
            <Explanation>{slopeSentence(report, fmt, strings)}</Explanation>
            <Explanation>
              {t(strings.r2Sentence, {
                r2: num(report.rSquared),
                pct: formatPercent(fmt, report.rSquared, 1),
              })}
            </Explanation>
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
            <ScatterPlot
              fmt={fmt}
              intercept={report.intercept}
              label={t(strings.plotLabel, { n: fmt(report.n), equation })}
              slope={report.slope}
              xLabel={strings.axisX}
              xs={data.xs}
              yLabel={strings.axisY}
              ys={data.ys}
            />
          </div>
          <Prediction fmt={fmt} report={report} strings={strings} />
          <CoefficientsTable fmt={fmt} report={report} strings={strings} />
          <AnovaTable fmt={fmt} report={report} strings={strings} />
          <ResidualsTable fmt={fmt} report={report} strings={strings} />
          <RegressionWorking fmt={fmt} report={report} strings={strings} />
        </section>
      ) : null}
      {report || showMismatch ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
